import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import { AddOnType, projectHookPreselect } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import {
    REDIRECT_BUTTON_TEXT,
    BUTTON_TEXT,
    FULL_BUTTON_TOOLTIP,
    SMALL_BUTTON_TOOLTIP,
} from "app/modules/project-from-comparison/components/ProjectCreationButton/text";
import {
    BUTTON_MIN_HEIGHT,
    BUTTON_MIN_WIDTH_COLLAPSED,
    BUTTON_MIN_WIDTH_REQUIRED,
    BUTTON_RIGHT_OFFSET,
    BUTTON_TOOLTIP_DELAY_MS,
    FAST_ANIMATION_DURATION_MS,
} from "app/modules/project-from-comparison/config";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { useAccessForButton } from "app/modules/project-from-comparison/hooks/useAccessForButton";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";

import * as SC from "./styles";

type CreationButtonSchema = {
    onClick: () => void;
};

export const ProjectCreationButton = ({ onClick }: CreationButtonSchema) => {
    const { getIsAddonAvailable } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const { isExist, hasFreeLimit, isWAPage, isProjectAllowed, hasCompetitors } = useAccessForButton();

    const containerRef = useRef<HTMLDivElement>(null);

    const [showText, setShowText] = useState<boolean>(true);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    const { translate } = useTranslate();

    const baseShowingCondition = isWAPage && isProjectAllowed && !isExist && hasCompetitors;
    const isRedirectButton = !hasFreeLimit && getIsAddonAvailable(AddOnType.AdditionalProjects);
    const shouldShowButton = baseShowingCondition && (hasFreeLimit || isRedirectButton);

    const tooltipContent = showText ? translate(FULL_BUTTON_TOOLTIP) : translate(SMALL_BUTTON_TOOLTIP);

    const config = useMemo(() => {
        return {
            dataAutomationContainer: isRedirectButton
                ? projectCreationDataAutomation.button.redirectContainer
                : projectCreationDataAutomation.button.container,
            dataAutomationButton: isRedirectButton
                ? projectCreationDataAutomation.button.redirectCta
                : projectCreationDataAutomation.button.cta,
            isTooltipEnabled: !isRedirectButton,
            buttonText: isRedirectButton ? REDIRECT_BUTTON_TEXT : BUTTON_TEXT,
        };
    }, [isRedirectButton]);

    useLayoutEffect(() => {
        if (!shouldRender || !containerRef.current) {
            return;
        }

        const checkTextFit = (): void => {
            const container = containerRef.current;

            if (!container) {
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const containerLeftPosition = containerRect.left;
            const maxRightPosition = window.innerWidth - BUTTON_RIGHT_OFFSET;
            const availableWidth = maxRightPosition - containerLeftPosition;
            const hasEnoughSpace = availableWidth >= BUTTON_MIN_WIDTH_REQUIRED;

            setShowText((prevShowText) => {
                if (prevShowText !== hasEnoughSpace) {
                    return hasEnoughSpace;
                }
                return prevShowText;
            });
        };

        const initialCheckTimeout = setTimeout(checkTextFit, FAST_ANIMATION_DURATION_MS + 50);

        const resizeObserver = new ResizeObserver(checkTextFit);
        resizeObserver.observe(document.body);

        return () => {
            clearTimeout(initialCheckTimeout);
            resizeObserver.disconnect();
        };
    }, [shouldRender]);

    useEffect(() => {
        if (shouldShowButton) {
            setShouldRender(true);
            const showTimer = setTimeout(() => {
                setIsVisible(true);
            }, 10);

            return () => clearTimeout(showTimer);
        } else {
            setIsVisible(false);
            const hideTimer = setTimeout(() => {
                setShouldRender(false);
            }, FAST_ANIMATION_DURATION_MS);

            return () => clearTimeout(hideTimer);
        }
    }, [shouldShowButton]);

    useEffect(() => {
        if (!shouldShowButton) {
            return;
        }

        projectCreatingTrack.trackSaveProjectCTAShow();
    }, [shouldShowButton]);

    const handleClick = (): void => {
        if (isRedirectButton) {
            projectCreatingTrack.trackSaveProjectRedirectCTAClick();
            handlePurchaseAddonClick(projectHookPreselect);
            return;
        }

        projectCreatingTrack.trackSaveProjectCTAClick();

        onClick();
    };

    const onTooltipOpen = (open: boolean): void => {
        if (!open) {
            setShowTooltip(false);

            return;
        }

        projectCreatingTrack.trackSaveProjectCTAHover();
        setShowTooltip(true);
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <SC.Container
            ref={containerRef}
            $isVisible={isVisible}
            data-automation={config.dataAutomationContainer}
        >
            <Tooltip
                content={tooltipContent}
                placement="bottom"
                delay={{ open: BUTTON_TOOLTIP_DELAY_MS }}
                onOpenChange={onTooltipOpen}
                open={showTooltip}
                enabled={config.isTooltipEnabled}
            >
                <IconButton
                    textCase="buttonText"
                    iconName="open-folder"
                    iconSize="xs"
                    iconColor={colorsPalettes.blue[400]}
                    type="outlined"
                    width={!showText ? BUTTON_MIN_WIDTH_COLLAPSED : undefined}
                    height={BUTTON_MIN_HEIGHT}
                    onClick={handleClick}
                    dataAutomation={config.dataAutomationButton}
                >
                    {showText ? translate(config.buttonText) : null}
                </IconButton>
            </Tooltip>
        </SC.Container>
    );
};
